/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\components\OptimisationModal.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT MODAL OPTIMISATION

📋 DESCRIPTION:
Composant React modal pour l'affichage détaillé de l'optimisation du calepinage.
Interface complète avec visualisation graphique et indicateurs d'économie.

🎯 RESPONSABILITÉS:
- Affichage modal plein écran des résultats d'optimisation
- Visualisation graphique du calepinage optimisé via DevisCalepinage
- Indicateurs d'économie avec montants détaillés
- Actions de gestion (recalculer, désactiver, fermer)
- Interface responsive avec layout adaptatif
- Intégration du composant de calepinage visuel

🔗 DÉPENDANCES:
- DevisCalepinage: Composant de visualisation graphique du calepinage
- DevisAdapter: Utilitaire de conversion des données pour calepinage
- types.ts: Interface Matiere pour typage
- React: Composant fonctionnel avec props

📡 INTERACTIONS:
- Parent: Réception données optimisation et callbacks actions
- DevisCalepinage: Affichage graphique des résultats optimisés
- Interface: Actions utilisateur (recalculer, désactiver, fermer)
- Adapter: Conversion données pour compatibilité calepinage

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Modal plein écran pour visualisation optimale
- Affichage conditionnel économie si significative (>0.01€)
- Intégration composant calepinage existant
- Actions contextuelles avec confirmations
- Layout responsive avec sections organisées
- Conversion automatique données via adapter
*/
import React, { useState, useEffect } from 'react';
import { DevisCalepinage } from '../../../../components/Calepinage';
import { convertToDevisDimensions } from '../../../../utils/DevisAdapter';
import { Matiere } from '../types';

interface OptimisationModalProps {
  isOpen: boolean;
  devisOptimise: any | null;
  matieres: Matiere[];
  isRefreshing?: boolean;
  devisId?: string;  // ✅ AJOUT: ID du devis pour charger les vignettes
  onClose: () => void;
  onRecalculer: () => void;
  onDesactiver: () => void;
}


const OptimisationModal: React.FC<OptimisationModalProps> = ({
  isOpen,
  devisOptimise,
  matieres,
  isRefreshing,
  devisId,  // ✅ AJOUT: Récupérer devisId des props
  onClose,
  onRecalculer,
  onDesactiver
}) => {
  const [forceReload, setForceReload] = useState(0);
  
  // Forcer le rechargement des données quand la modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setForceReload(prev => prev + 1);
    }
  }, [isOpen]);

  if (!isOpen || !devisOptimise) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl h-full max-h-none flex flex-col">
        {/* En-tête de la modal */}
        <div className="flex items-center justify-between p-6 border-b bg-gray-50 rounded-t-lg">
          <h3 className="text-2xl font-semibold text-gray-900">
            Optimisation du calepinage - Détail complet
          </h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium transition-colors duration-200"
                onClick={onRecalculer}
              >
                Recalculer
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium transition-colors duration-200"
                onClick={() => {
                  onDesactiver();
                  onClose();
                }}
              >
                Désactiver
              </button>
            </div>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-200 rounded-full"
              onClick={onClose}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Contenu de la modal - FLEXIBLE */}
        <div className="flex-1 overflow-auto p-6">
          {Math.abs(Number(devisOptimise.economieTotal)) > 0.01 && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium text-green-800">Économie réalisée</h3>
                  <p className="text-sm text-green-600">Optimisation du placement des pièces sur les matières</p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-green-600">Économie totale</div>
                  <div className="text-2xl font-bold text-green-700">
                    {devisOptimise.economieTotal !== undefined 
                      ? (-devisOptimise.economieTotal).toFixed(2) 
                      : '0.00'} €
                  </div>
                </div>
              </div>
            </div>
          )}

          <DevisCalepinage 
            devis={{
              ...convertToDevisDimensions(devisOptimise),
              id: devisId || devisOptimise.devis_id || ''  // ✅ CORRECTION: Ajouter devisId pour charger vignettes
            }}
            matieres={matieres}
            isRefreshing={isRefreshing}
          />
        </div>
        
        {/* Pied de modal */}
        <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            type="button"
            className="px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200 font-medium"
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default OptimisationModal;
